import Auth from "src/Auth";

/** @typedef {{ capturedAt: string, payload: Record<string, unknown> }} StoredDraftPreflightSnapshot */

export const DRAFT_PREFLIGHT_SNAPSHOT_KEY = "draftPreflightSnapshot";

/**
 * Saves latest Draft preflight analysis so server-side agent reads can include it.
 * Loads current scenes from the API first so we never overwrite Postgres `value` with an empty
 * or stale merge when the Story Bible store has not caught up.
 *
 * Uses optimistic locking: sends `updatedAt` from the GET in the PUT, and retries once on 409.
 *
 * @param {string} documentId
 * @param {Record<string, unknown>} preflightPayload analyze + estimates object from Plan Preflight
 */
export async function persistDraftPreflightSnapshot(documentId, preflightPayload) {
  const attemptPersist = async () => {
    const current = await Auth.fetchWithIdToken(`/api/scenes/`, { documentId });
    const base =
      current?.value && typeof current.value === "object" && !Array.isArray(current.value)
        ? { ...current.value }
        : {};
    base[DRAFT_PREFLIGHT_SNAPSHOT_KEY] = {
      capturedAt: new Date().toISOString(),
      payload: preflightPayload,
    };
    const updated = await Auth.fetchWithIdToken(`/api/scenes/`, {
      method: "PUT",
      documentId,
      value: base,
      ...(current?.updatedAt ? { updatedAt: current.updatedAt } : {}),
    });
    return updated;
  };

  try {
    return await attemptPersist();
  } catch (error) {
    if (error?.status === 409) {
      return attemptPersist(); // retry once with fresh GET
    }
    throw error;
  }
}

/**
 * Removes persisted preflight so agents do not see stale warnings after scenes reset.
 *
 * Uses optimistic locking: sends `updatedAt` from the GET in the PUT, and retries once on 409.
 *
 * @returns {Promise<unknown|null>} Updated scenes entity, or null if nothing was stored / skipped
 */
export async function clearDraftPreflightSnapshot(documentId) {
  const attemptClear = async () => {
    const current = await Auth.fetchWithIdToken(`/api/scenes/`, { documentId });
    if (!current?.value?.[DRAFT_PREFLIGHT_SNAPSHOT_KEY]) {
      return null;
    }
    const base =
      current.value && typeof current.value === "object" && !Array.isArray(current.value)
        ? { ...current.value }
        : {};
    delete base[DRAFT_PREFLIGHT_SNAPSHOT_KEY];
    const updated = await Auth.fetchWithIdToken(`/api/scenes/`, {
      method: "PUT",
      documentId,
      value: base,
      ...(current?.updatedAt ? { updatedAt: current.updatedAt } : {}),
    });
    return updated;
  };

  try {
    return await attemptClear();
  } catch (error) {
    if (error?.status === 409) {
      return attemptClear(); // retry once with fresh GET
    }
    throw error;
  }
}
